/**
 * NoteEditForm - a typed note edited in place (docs/plans/crm-report-builder-search-collab-plan.md A1.3
 * step 1, A1.4, B1.4; WP N-7). The note's text in a box with Save and Cancel; Save asks why
 * (NoteReasonDialog), then PATCH /contacts/:id/notes/:noteId with expected_edit_count, so a teammate's
 * change in the meantime comes back as the 409 and the form says so in the recruitment list's words:
 * "Dana Smith edited this note just now", their text, Keep theirs / Save mine.
 *
 *   - Save is off until the text differs from the note (after trimming, the server's comparison), is
 *     not empty and is at most 5,000 characters.
 *   - Esc cancels and Ctrl+Enter (Cmd+Enter) saves. Both are read in the bubble phase on the form and
 *     stopped there, so the call workspace around the thread stays open; an input inside that consumes
 *     Esc itself (the mention picker) stops it first or marks it handled.
 *   - Keep theirs hands the caller the note with their text (outcome 'kept_theirs', no request); Save
 *     mine sends the same reason again with their edit_count (none when the server gave none: a
 *     deliberate overwrite).
 *   - Any other refusal shows the server's sentence; a refusal no retry can fix (deleted meanwhile,
 *     past the 24 hours, not editable, not found) turns Save off.
 *
 * renderInput and mentionsFor are the seam for @mentions (X-3 passes M-6's MentionInput and
 * extractMentions over its picked list); without them the form is a plain text box and sends no
 * `mentions` key.
 */
import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import type { Communication } from '../types';
import { Alert, btnGhost, btnPrimary, fieldCls } from '../components/ui';
import { editNote, noteErrorOf } from './api';
import { NOTE_BODY_MAX } from './noteLogic';
import type { NoteWordsOptions } from './noteLogic';
import NoteReasonDialog, { NoteConflictNotice } from './NoteReasonDialog';
import type { NoteReasonChoice } from './NoteReasonDialog';
import type { NoteConflict, NoteEditRequest, NoteError, NoteErrorCode } from './types';

/** How an edit ended: written, the same text (nothing written), or the teammate's text kept. */
export type NoteEditOutcome = 'saved' | 'unchanged' | 'kept_theirs';

/** What a custom text box (renderInput) receives. */
export interface NoteEditInputProps {
  /** The box's id; the form's hidden label points at it. */
  id: string;
  value: string;
  onChange: (next: string) => void;
  /** True while the save is in flight. */
  disabled: boolean;
  /** Take the keyboard when mounted, the caret at the end. */
  autoFocus: boolean;
  placeholder: string;
  /** The default box's classes, for a matching look. */
  className: string;
}

export interface NoteEditFormProps {
  contactId: string;
  /** The typed note being edited (its body, and the edit_count sent as expected_edit_count). */
  note: Communication;
  /**
   * The edit is over: the note as it is now (patch the thread with patchCommunication) and how it
   * ended. The caller closes the form.
   */
  onSaved: (note: Communication, outcome: NoteEditOutcome) => void;
  /** Cancel or Esc: nothing was written. The caller closes the form. */
  onCancel: () => void;
  /** Rendered at the left of the Save / Cancel row. */
  extraControls?: ReactNode;
  /** Replaces the plain text box (X-3: M-6's MentionInput). */
  renderInput?: (input: NoteEditInputProps) => ReactNode;
  /** The user ids the text names with "@", read at each save; undefined sends no `mentions` key. */
  mentionsFor?: (text: string) => string[] | null | undefined;
  /** A user id to a name, for the 409 line (useTeam().byId); a name the server sent wins. */
  nameOf?: NoteWordsOptions['nameOf'];
}

/** Refusals a retry cannot fix: Save stays off, Cancel remains. */
const TERMINAL: ReadonlySet<NoteErrorCode> = new Set<NoteErrorCode>(['deleted', 'not_allowed', 'not_editable', 'not_found']);

/** Show the count from this many characters on. */
const COUNT_FROM = NOTE_BODY_MAX - 500;

const BOX_CLS = `${fieldCls} block min-h-[88px] w-full resize-y`;

const editCountOf = (c: Pick<Communication, 'edit_count'>): number =>
  typeof c.edit_count === 'number' && Number.isFinite(c.edit_count) ? c.edit_count : 0;

export default function NoteEditForm({
  contactId,
  note,
  onSaved,
  onCancel,
  extraControls,
  renderInput,
  mentionsFor,
  nameOf,
}: NoteEditFormProps) {
  const original = note.body ?? '';
  const [text, setText] = useState(original);
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<NoteError | null>(null);
  const [conflict, setConflict] = useState<NoteConflict | null>(null);
  const [lastReason, setLastReason] = useState<NoteReasonChoice | null>(null);
  // The edit_count the save is checked against: the note's, then the teammate's after a 409 (null =
  // the server gave none, so "Save mine" saves without the check).
  const [expected, setExpected] = useState<number | null>(() => editCountOf(note));
  const boxRef = useRef<HTMLTextAreaElement>(null);
  const alive = useRef(true);
  const boxId = useId();

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // The plain box takes the keyboard with the caret after the text.
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    box.focus();
    const end = box.value.length;
    box.setSelectionRange(end, end);
  }, []);

  const trimmed = text.trim();
  const length = Array.from(trimmed).length;
  const empty = trimmed === '';
  const tooLong = length > NOTE_BODY_MAX;
  const unchanged = trimmed === original.trim();
  const terminal = error !== null && TERMINAL.has(error.code);
  const canSave = !busy && !empty && !tooLong && !unchanged && !terminal && !conflict;

  const send = async (reason: NoteReasonChoice, expectedCount: number | null) => {
    setBusy(true);
    setError(null);
    const req: NoteEditRequest = { body: trimmed, ...reason };
    if (expectedCount !== null) req.expected_edit_count = expectedCount;
    try {
      const ids = mentionsFor ? mentionsFor(text) : undefined;
      if (Array.isArray(ids)) req.mentions = ids;
      const res = await editNote(contactId, note.id, req);
      if (!alive.current) return;
      setBusy(false);
      onSaved(res.note ?? { ...note, body: trimmed }, res.unchanged ? 'unchanged' : 'saved');
    } catch (e) {
      if (!alive.current) return;
      setBusy(false);
      const err = noteErrorOf(e);
      if (err.code === 'note_changed' && err.conflict) {
        setConflict(err.conflict);
        setExpected(err.conflict.current.edit_count);
        setLastReason(reason);
      } else {
        setError(err);
      }
    }
  };

  const requestSave = () => {
    if (!canSave) return;
    setError(null);
    setAsking(true);
  };

  const onReason = (reason: NoteReasonChoice) => {
    setAsking(false);
    setLastReason(reason);
    void send(reason, expected);
  };

  const saveMine = () => {
    if (!lastReason || busy || empty || tooLong) return;
    setConflict(null);
    void send(lastReason, expected);
  };

  const keepTheirs = () => {
    if (!conflict) return;
    const cur = conflict.current;
    const theirs: Communication = {
      ...note,
      body: cur.body ?? note.body,
      edited_at: cur.edited_at ?? note.edited_at ?? null,
      edited_by: cur.edited_by ?? note.edited_by ?? null,
    };
    if (cur.edit_count !== null) theirs.edit_count = cur.edit_count;
    onSaved(theirs, 'kept_theirs');
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.defaultPrevented) return;
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (!busy) onCancel();
      return;
    }
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !e.nativeEvent.isComposing) {
      e.preventDefault();
      e.stopPropagation();
      requestSave();
    }
  };

  let saveTitle: string | undefined;
  if (unchanged) saveTitle = 'Change the text to save';
  else if (empty) saveTitle = 'A note needs some text';
  else if (tooLong) saveTitle = `A note is at most ${NOTE_BODY_MAX.toLocaleString()} characters`;

  const input: NoteEditInputProps = {
    id: boxId,
    value: text,
    onChange: setText,
    disabled: busy,
    autoFocus: true,
    placeholder: 'Write the note',
    className: BOX_CLS,
  };

  return (
    <>
      <div className="space-y-2" onKeyDown={onKeyDown} data-note-edit={note.id}>
        <label htmlFor={boxId} className="sr-only">
          Edit note
        </label>
        {renderInput ? (
          renderInput(input)
        ) : (
          <textarea
            id={boxId}
            ref={boxRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={busy}
            rows={4}
            placeholder={input.placeholder}
            className={BOX_CLS}
          />
        )}

        {length >= COUNT_FROM && (
          <p
            className={`text-right text-[11px] tabular-nums ${
              tooLong ? 'font-medium text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'
            }`}
          >
            {length.toLocaleString()} / {NOTE_BODY_MAX.toLocaleString()}
            {tooLong ? ': too long to save' : ''}
          </p>
        )}

        {conflict && (
          <NoteConflictNotice conflict={conflict} nameOf={nameOf}>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button type="button" onClick={keepTheirs} disabled={busy} className={`${btnGhost} bg-white dark:bg-gray-800`}>
                Keep theirs
              </button>
              <button type="button" onClick={saveMine} disabled={busy || empty || tooLong} className={btnPrimary}>
                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
                Save mine
              </button>
            </div>
          </NoteConflictNotice>
        )}

        {error && (
          <Alert tone="error" size="sm">
            {error.message}
          </Alert>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            {extraControls}
            <span className="hidden text-[11px] text-gray-500 dark:text-gray-400 sm:inline">
              Ctrl+Enter to save, Esc to cancel
            </span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button type="button" onClick={onCancel} disabled={busy} className={`${btnGhost} bg-white dark:bg-gray-800`}>
              Cancel
            </button>
            {!conflict && (
              <button type="button" onClick={requestSave} disabled={!canSave} title={saveTitle} className={btnPrimary}>
                {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
                Save
              </button>
            )}
          </div>
        </div>
      </div>
      <NoteReasonDialog open={asking} mode="edit" onConfirm={onReason} onCancel={() => setAsking(false)} />
    </>
  );
}
